/**
 * JSON Validator and Fixer for Artifact
 * 
 * This utility helps validate and fix common JSON issues in discover.json files
 * for the Artifact platform.
 */

// Function to validate and fix JSON
export function validateAndFixJson(jsonString) {
    try {
      // Test if it's already valid JSON
      JSON.parse(jsonString);
      console.log("JSON is valid");
      return { valid: true, json: jsonString };
    } catch (error) {
      console.log("JSON is invalid:", error.message);
      
      // Common error fixing attempts
      let fixedJson = jsonString;
      
      // Check for HTML/XML content
      if (fixedJson.includes('<') && fixedJson.includes('>')) {
        console.log("Detected HTML/XML content in JSON file");
        
        // If the file starts with HTML tags, it might be completely wrong format
        if (fixedJson.trim().startsWith('<')) {
          console.log("File appears to be HTML instead of JSON");
          // Create a basic empty discover.json structure
          return { 
            valid: false, 
            json: JSON.stringify({ entries: [] }, null, 2),
            message: "Original file contained HTML. Created empty JSON structure."
          };
        }
      }
      
      // Check for common JSON syntax errors
      
      // 1. Missing quotes around property names
      fixedJson = fixedJson.replace(/(\s*)(\w+)(\s*):/g, '$1"$2"$3:');
      
      // 2. Replace single quotes with double quotes (but not within string content)
      // This is a simplified approach - a full parser would be better
      fixedJson = fixedJson.replace(/'([^']*)'(?=\s*:)/g, '"$1"');
      fixedJson = fixedJson.replace(/:\s*'([^']*)'/g, ': "$1"');
      
      // 3. Check for trailing commas in arrays and objects
      fixedJson = fixedJson.replace(/,(\s*[\]}])/g, '$1');
      
      // 4. Fix missing commas between array items or object properties
      // This is complex and might require a more sophisticated parser
      
      // 5. Ensure the file has proper enclosing brackets
      fixedJson = fixedJson.trim();
      if (!fixedJson.startsWith('{') && !fixedJson.startsWith('[')) {
        fixedJson = '{' + fixedJson;
      }
      if (!fixedJson.endsWith('}') && !fixedJson.endsWith(']')) {
        fixedJson = fixedJson + '}';
      }
      
      // Test if our fixes worked
      try {
        JSON.parse(fixedJson);
        return { 
          valid: true, 
          json: fixedJson,
          message: "Fixed JSON successfully"
        };
      } catch (secondError) {
        return { 
          valid: false, 
          json: JSON.stringify({ entries: [] }, null, 2),
          message: "Could not fix JSON: " + secondError.message
        };
      }
    }
  }
  
  // Function to convert discover.json to correct format
  export function convertToDiscoverFormat(jsonObject) {
    // If it's already in the correct format with entries property
    if (jsonObject.entries && Array.isArray(jsonObject.entries)) {
      // Ensure each entry has the correct properties
      jsonObject.entries = jsonObject.entries.map(entry => {
        // Convert imageUrl to image if needed
        if (entry.imageUrl && !entry.image) {
          entry.image = entry.imageUrl;
          delete entry.imageUrl;
        }
        
        // Ensure tags is an array
        if (!entry.tags) {
          entry.tags = [];
        } else if (!Array.isArray(entry.tags)) {
          entry.tags = [entry.tags];
        }
        
        // Ensure all required properties exist
        return {
          title: entry.title || "",
          description: entry.description || "",
          tags: entry.tags,
          image: entry.image || "",
          source: entry.source || ""
        };
      });
      
      return jsonObject;
    }
    
    // If it's an array but not wrapped in an entries property
    if (Array.isArray(jsonObject)) {
      return {
        entries: jsonObject.map(item => {
          // Convert imageUrl to image if needed
          if (item.imageUrl && !item.image) {
            item.image = item.imageUrl;
            delete item.imageUrl;
          }
          
          // Ensure tags is an array
          if (!item.tags) {
            item.tags = [];
          } else if (!Array.isArray(item.tags)) {
            item.tags = [item.tags];
          }
          
          return {
            title: item.title || "",
            description: item.description || "",
            tags: item.tags,
            image: item.image || "",
            source: item.source || ""
          };
        })
      };
    }
    
    // If it's some other format
    return {
      entries: []
    };
  }